import {datadogRum} from "@datadog/browser-rum"

import {wrappedGet} from "./AxiosHelper"

const tokenKeys = [
    "access_token",
    "user_id",
    "display_name",
    "user_name",
    "role",
    "unit_name",
    "is_admin",
    "organization_id",
]

// Everything that belongs to one login session. Cleared before a new login is saved.
const sessionKeys = [...tokenKeys, "real_user_id", "acting_as"]

const AUTH_METHOD_KEY = "auth_method"

// Store a value, or remove the key when there is no value, so we never
// end up with the literal string "undefined" in sessionStorage.
const setItem = (key, value) => {
    if (value === undefined || value === null) {
        sessionStorage.removeItem(key)
    } else {
        sessionStorage.setItem(key, value)
    }
}

export const decodeJwtPayload = (jwt) => {
    if (!jwt || typeof jwt !== "string") {
        return null
    }

    const parts = jwt.split(".")
    if (parts.length !== 3) {
        return null
    }

    try {
        const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/")
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
        const binary = atob(padded)
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
        return JSON.parse(new TextDecoder().decode(bytes))
    } catch (error) {
        console.warn("Unable to decode JWT payload", error)
        return null
    }
}

export const getJwtExpiryTime = () => {
    const token = sessionStorage.getItem("access_token")
    if (!token) {
        return null
    }

    const payload = decodeJwtPayload(token)
    if (!payload || payload.exp === undefined || Number.isNaN(Number(payload.exp))) {
        return null
    }

    return Number(payload.exp) * 1000
}

export const isJwtExpired = () => {
    const expiryTime = getJwtExpiryTime()
    if (expiryTime === null) {
        return false
    }

    return expiryTime <= Date.now()
}

export const getToken = (allowMissing) => {
    const token = Object.fromEntries(tokenKeys.map((key) => [key, sessionStorage.getItem(key)]))
    const authMethod = sessionStorage.getItem(AUTH_METHOD_KEY)

    // The browser can be authenticated either by a bearer JWT in sessionStorage,
    // or by an OIDC cookie flow that populates the user profile through /api/me.
    const isAuthenticated = !!token.access_token || authMethod === "cookie"

    if (isAuthenticated) {
        return token
    }

    if (allowMissing) {
        return {}
    }

    removeToken()
    window.location.href = "/login"
    return {}
}

export const saveToken = (userToken) => {
    // Drop anything left over from a previous session (e.g. real_user_id / acting_as)
    sessionKeys.forEach((key) => sessionStorage.removeItem(key))
    sessionKeys.forEach((key) => setItem(key, userToken[key]))
    // Password flow uses Bearer header
    sessionStorage.setItem(AUTH_METHOD_KEY, "bearer")
}

export const removeToken = () => {
    sessionStorage.clear()
    datadogRum.clearUser()
}

export const renewToken = (access_token) => {
    if (access_token) {
        sessionStorage.setItem("access_token", access_token)
    }
}

export const getAuthMethod = () => {
    return sessionStorage.getItem(AUTH_METHOD_KEY)
}

export const handleExpiredJwtSession = () => {
    removeToken()
    const redirectTo = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?redirectTo=${redirectTo}`)
}

/**
 * Populate sessionStorage from a cookie-based JWT (OIDC flow).
 *
 * If /api/me succeeds, the user is logged in via cookie — populate
 * user metadata in sessionStorage and mark auth_method as "cookie".
 * Returns user object on success, null on failure.
 */
export const bootstrapFromCookie = async () => {
    // If already authenticated via cookie, nothing to do — but return a
    // truthy value so callers know they're authenticated.
    const authMethod = getAuthMethod()
    if (authMethod === "cookie") {
        return {already_authenticated: true}
    }
    // If user logged in via password (Bearer), bootstrap doesn't apply
    if (authMethod === "bearer") {
        return null
    }
    try {
        const user = await refreshCurrentUser()
        if (!user) {
            return null
        }
        datadogRum.setUser({
            id: user.user_id,
            name: user.user_name,
            email: user.email,
            role: user.role,
        })
        return user
    } catch (error) {
        return null
    }
}

export const refreshCurrentUser = async () => {
    try {
        const response = await wrappedGet("me")
        const user = response.data

        setItem("user_id", user.user_id)
        setItem("display_name", user.display_name)
        setItem("user_name", user.user_name)
        setItem("is_admin", user.is_admin)
        setItem("role", user.role)
        setItem("unit_name", user.unit_name || "")
        // When adopting another user, use that user's organization
        setItem("organization_id", user.acting_as_organization_id || user.organization_id || "")
        setItem("real_user_id", user.real_user_id)
        setItem("acting_as", user.acting_as)
        sessionStorage.setItem(AUTH_METHOD_KEY, "cookie")

        // Only overwrite the stored token when /me actually returned one
        if (user.access_token) {
            sessionStorage.setItem("access_token", user.access_token)
        }
        return user
    } catch (error) {
        console.warn(error)
        return null
    }
}

export const checkIsAdopting = () => {
    const realUserId = sessionStorage.getItem("real_user_id")
    const actingAs = sessionStorage.getItem("acting_as")

    return !!(realUserId && actingAs && realUserId !== actingAs)
}

export const getPermissions = () => {
    const token = sessionStorage.getItem("access_token")
    if (!token) {
        return {}
    }

    const payload = decodeJwtPayload(token)

    return payload?.permissions || {}
}
